// usePusherChannel — subscribe to a private channel and collect typed events.
// Server→client push is Pusher ONLY (doctrine 1.2): no SSE, no polling. Works
// against Soketi locally and real Pusher in cloud (same client, different host).

import { useEffect, useRef, useState } from "react";

import { loadPusherConnection, withPusherConnection } from "./load-pusher-connection";
import { emitPusherHealth } from "./pusher-health";

// The connection manager and pusher-js are lazy (./load-pusher-connection). Start their
// download now, with the module that uses them, so the first subscribe rarely waits.
void loadPusherConnection().catch(() => undefined);

export interface PusherChannelConfig {
  key: string;
  host: string;
  port: number;
  useTls: boolean;
  /** Required by pusher-js even for self-hosted Soketi; ignored when host is set. */
  cluster?: string;
  /** The /api/pusher/auth endpoint that signs private-channel subscriptions. */
  authEndpoint: string;
  /** Headers the auth POST must carry (stub identity). */
  authHeaders?: Record<string, string>;
  /**
   * Close the WebSocket after the tab has been hidden this many ms, then reopen
   * it (re-subscribing + reconciling via `onReconnect`) when it becomes visible
   * again. Idle/backgrounded tabs otherwise hold their socket open indefinitely —
   * pusher-js answers the server's liveness pings, so the server never reaps them —
   * inflating the concurrent-connection count Pusher bills and caps on, out of
   * proportion to actually-active users. Defaults to 30s; set to `0` to disable
   * (e.g. an operator spectate/debug tab that must stay hot). The policy is
   * per-connection, taken from the first consumer that opens the shared socket.
   */
  idleDisconnectMs?: number;
}

/**
 * Subscribe to `channelName` (or nothing when null) and return events received on
 * any of `eventNames`, in arrival order. Re-subscribes when the channel changes.
 * On unmount it releases its lease on the shared, ref-counted connection
 * (`./pusher-connection`).
 *
 * `onReconnect` (optional) fires on every RE-subscribe (the 2nd+
 * `pusher:subscription_succeeded`), never the first. Pusher does NOT replay events
 * published while the socket was down (a network blip, a transport upgrade, the
 * idle-tab disconnect), so a consumer driven by those events re-reads state over
 * REST here. Without it, a turn whose terminal event fell in the gap hangs until
 * a manual refresh.
 *
 * `onSubscriptionError` (optional) fires on EVERY `pusher:subscription_error`,
 * including the initial one that `onReconnect` skips. An initial auth failure is
 * how a client/server identity split presents (`buyer_channel_mismatch`). Without
 * this hook the consumer silently receives nothing. Consumers use it to recheck
 * identity or reload.
 *
 * Both callbacks are passed via refs, so changing them never re-subscribes.
 */
export function usePusherChannel<E>(
  channelName: string | null,
  eventNames: readonly string[],
  config: PusherChannelConfig,
  onReconnect?: () => void,
  onSubscriptionError?: () => void,
): E[] {
  const [events, setEvents] = useState<E[]>([]);
  const headersKey = JSON.stringify(config.authHeaders ?? {});
  const namesKey = eventNames.join(",");

  // Keep the latest callback without re-running the subscribe effect (which would
  // tear down + rebuild the connection on every render that passes a new closure).
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;
  const onSubscriptionErrorRef = useRef(onSubscriptionError);
  onSubscriptionErrorRef.current = onSubscriptionError;

  useEffect(() => {
    setEvents([]);
    if (!channelName) return;

    // Join (or create) the shared, ref-counted connection + channel. This hook
    // never constructs its own pusher-js instance — that would open a redundant
    // socket per consumer to the same channel. Synchronous once the manager has
    // loaded; before that, it joins when the manager lands.
    return withPusherConnection(({ acquireChannel }) => {
      const lease = acquireChannel(config, channelName);
      const channel = lease.channel;
      const handler = (data: E): void => setEvents((prev) => [...prev, data]);
      for (const name of eventNames) channel.bind(name, handler);

      // The FIRST success is the initial subscribe (bootstrap already loaded state).
      // Every later one is a reconnect. A late joiner to an already-subscribed
      // shared channel has had its initial subscribe, so seed `subscribedOnce` true.
      let subscribedOnce = lease.wasAlreadySubscribed;
      const onSubscribed = (): void => {
        if (subscribedOnce) onReconnectRef.current?.();
        subscribedOnce = true;
      };
      channel.bind("pusher:subscription_succeeded", onSubscribed);

      // Log every failed (re-)subscribe (doctrine 1.5). On a RE-subscribe failure
      // (e.g. an SSO cookie that expired while the tab was hidden), also nudge a
      // REST re-read so it surfaces there, not as a stuck turn. Not on an initial
      // failure: that would double-read on bootstrap.
      const onSubscriptionError = (status: unknown): void => {
        console.error("[usePusherChannel] subscription_error", channelName, status);
        emitPusherHealth({ kind: "subscription_error", detail: channelName ?? undefined });
        if (subscribedOnce) onReconnectRef.current?.();
        // Unlike onReconnect this fires on the INITIAL failure too — an identity
        // split (auth 403) never subscribes at all, and the consumer must hear
        // about it to self-heal rather than silently receive nothing.
        onSubscriptionErrorRef.current?.();
      };
      channel.bind("pusher:subscription_error", onSubscriptionError);

      return () => {
        channel.unbind("pusher:subscription_succeeded", onSubscribed);
        channel.unbind("pusher:subscription_error", onSubscriptionError);
        for (const name of eventNames) channel.unbind(name, handler);
        lease.release();
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    channelName,
    config.key,
    config.host,
    config.port,
    config.useTls,
    config.cluster,
    config.authEndpoint,
    config.idleDisconnectMs,
    headersKey,
    namesKey,
  ]);

  return events;
}
